import { OperatorFunction } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * DRF's global PageNumberPagination (see the API's settings.py —
 * DEFAULT_PAGINATION_CLASS + PAGE_SIZE: 10) wraps every list endpoint in an
 * envelope. Every marketplace service list method was typed as a bare array
 * and assigned straight to a component field, so `@for`/`*ngFor` received an
 * object and threw "newCollection[Symbol.iterator] is not a function",
 * rendering every storefront page empty.
 */
export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export type MaybePaginated<T> = Paginated<T> | T[] | null | undefined;

export function isPaginated<T>(res: MaybePaginated<T>): res is Paginated<T> {
  return !!res && !Array.isArray(res) && Array.isArray((res as Paginated<T>).results);
}

/**
 * Normalises a list response to a plain array.
 *
 * Tolerates both shapes on purpose: the real backend returns the envelope,
 * but PreviewInterceptor (`?preview=1`) answers with bare array fixtures.
 * Both have to keep working, so never assume one or the other.
 */
export function unwrapPage<T>(): OperatorFunction<MaybePaginated<T>, T[]> {
  return map((res) => (isPaginated(res) ? res.results : Array.isArray(res) ? res : []));
}

/** Total count when the backend reports one, else the length we actually got. */
export function pageCount<T>(res: MaybePaginated<T>): number {
  return isPaginated(res) ? res.count : Array.isArray(res) ? res.length : 0;
}
